import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface CreateDatasetDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (
		name: string,
		datasetKind: "behavior_examples" | "operational_rules",
	) => void;
	initialDatasetKind?: "behavior_examples" | "operational_rules";
}

export const CreateDatasetDialog = ({
	open,
	onOpenChange,
	onSubmit,
	initialDatasetKind,
}: CreateDatasetDialogProps) => {
	const [name, setName] = useState("");
	const [datasetKind, setDatasetKind] = useState<
		"behavior_examples" | "operational_rules"
	>(initialDatasetKind || "behavior_examples");

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (name.trim()) {
			onSubmit(name.trim(), datasetKind);
			setName("");
			setDatasetKind("behavior_examples");
			onOpenChange(false);
		}
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			setName("");
			setDatasetKind(initialDatasetKind || "behavior_examples");
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[425px]">
				<form onSubmit={handleSubmit}>
					<DialogHeader>
						<DialogTitle>Create New Dataset</DialogTitle>
						<DialogDescription>
							Enter a name and select the type of dataset you want to create.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label htmlFor="name">Dataset Name</Label>
							<Input
								id="name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="Enter dataset name"
								autoFocus
							/>
						</div>
						<div className="grid gap-2">
							<Label>Dataset Type</Label>
							<RadioGroup
								value={datasetKind}
								onValueChange={(value) =>
									setDatasetKind(
										value as "behavior_examples" | "operational_rules",
									)
								}
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem
										value="behavior_examples"
										id="behavior_examples"
									/>
									<Label
										htmlFor="behavior_examples"
										className="font-normal cursor-pointer"
									>
										Behavior Examples
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem
										value="operational_rules"
										id="operational_rules"
									/>
									<Label
										htmlFor="operational_rules"
										className="font-normal cursor-pointer"
									>
										Rules
									</Label>
								</div>
							</RadioGroup>
						</div>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim()}>
							Create Dataset
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
